import { UserDTO } from '@ytl/common-model';
import { Box, Button, errorIcon, successIcon, useTheme } from '@ytl/common-web';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReactComponent as FailedIcon } from '../assets/PageNotFound2.svg';
import { ReactComponent as SuccessIcon } from '../assets/SuccessGirl.svg';
import { selectCurrentUser } from '../contexts/currentUserSlicer';
import { useLanguage } from '../contexts/languageContext';
import { VerificationComponent } from '../modules/verificationComponent/VerificationComponent';
import { AppRoutes } from '../routes';
import { WidgetEvent } from '../shared/@types/widgetEvents';
import { Banned } from '../shared/components/partials/verification/Banned';
import { getConfig } from '../shared/data/config';
import { accountResource } from '../shared/resource/account.resource';
import { onProcessPhase, onWidgetEvent } from '../shared/utils/emitData';
import { createUnsignedJWT } from '../shared/utils/jwtUtils';

enum PhoneChangePageState {
  PhoneVerification,
  EmailVerification,
  Success,
  Error,
  Banned,
}

enum PhoneChangePhase {
  PhoneNumberVerificationCA = 'phoneNumberVerificationCA',
  PhoneNumberVerificationOther = 'phoneNumberVerificationOther',
  PhoneNumberVerificationCode = 'phoneNumberVerificationCode',
  Finish = 'finish',
  Banned = 'banned',
  Error = 'error',
}

export const PhoneChangePage = () => {
  const { translation } = useLanguage();
  const { isWebView } = useTheme();
  const [state, setState] = useState<PhoneChangePageState>(PhoneChangePageState.PhoneVerification);
  const currentUser = useSelector(selectCurrentUser) as UserDTO;
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>([]);
  const [verifiedMsisdn, setVerifiedMsisdn] = useState<string>('');
  const [verificatorJwtMsisdn, setVerificatorJwtMsisdn] = useState<string>('');

  useLayoutEffect(() => {
    if (isWebView) {
      document.documentElement.classList.add('ytl--app');
    }
    return () => {
      document.documentElement.classList.remove('ytl--app');
    };
  }, [isWebView, state]);

  // JWT generálása phone verification beállításokkal
  const phoneInitParamsJWT = useMemo(() => {
    const payload = {
      clientID: 'phone-change-client',
      sessionID: 'phone-change-session-' + Date.now(),
      withPassword: false,
      captchaNeeded: true,
      EmailVerification: false,
      TMSVerification: false,
      MSISDNVerification: true,
      createDUP: false,
      salesMsisdn: '',
      salesCA: '',
      IPBanningEnabled: false,
      language: '',
      theme: '',
      showLoginButton: false,
      additionalInfo: {
        application: isWebView ? 'APP' : 'ALM',
        channel_type: isWebView ? 'mobil_app' : 'web',
        process: 'Kapcsolattartási adatmódosítás',
        sales_type_cd: '7001',
        account_id: currentUser?.accountId || '',
        ca_id: currentUser?.assignedCustomers[0]?.customerAccountId || '',
      },
    };
    return createUnsignedJWT(payload);
  }, [currentUser, isWebView]);

  // JWT generálása email verification beállításokkal
  const emailInitParamsJWT = useMemo(() => {
    const payload = {
      clientID: 'phone-change-email-client',
      sessionID: 'phone-change-email-session-' + Date.now(),
      withPassword: false,
      captchaNeeded: false,
      EmailVerification: true,
      TMSVerification: false,
      MSISDNVerification: false,
      createDUP: false,
      salesMsisdn: '',
      salesCA: '',
      IPBanningEnabled: false,
      language: '',
      theme: '',
      showLoginButton: false,
      additionalInfo: {
        application: isWebView ? 'APP' : 'ALM',
        channel_type: isWebView ? 'mobil_app' : 'web',
        process: 'Kapcsolattartási adatmódosítás',
        sales_type_cd: '7001',
        account_id: currentUser?.accountId || '',
        ca_id: currentUser?.assignedCustomers[0]?.customerAccountId || '',
      },
    };
    return createUnsignedJWT(payload);
  }, [currentUser, isWebView]);

  const [view, setView] = useState<PhoneChangePhase>(PhoneChangePhase.PhoneNumberVerificationCA);

  useEffect(() => {
    const cleanup = onProcessPhase((phase: string) => {
      console.log('Received phase change:', phase);

      switch (phase) {
        case 'SelectPhoneNumber':
          setView(PhoneChangePhase.PhoneNumberVerificationCA);
          break;
        case 'AddDifferentPhoneNumber':
          setView(PhoneChangePhase.PhoneNumberVerificationOther);
          break;
        case 'PhoneNumberConfirm':
          setView(PhoneChangePhase.PhoneNumberVerificationCode);
          break;
        case 'Finish':
          setView(PhoneChangePhase.Finish);
          break;
        case 'Error':
          setView(PhoneChangePhase.Error);
          break;
      }
    });

    return cleanup;
  }, []);

  useEffect(() => {
    const fetchPhoneNumbers = async () => {
      if (!currentUser?.email) return;

      try {
        const phoneNumbers = await accountResource.searchCustomerContactPhoneNumbers(currentUser.email);
        setPhoneNumbers(phoneNumbers as string[]);
      } catch (error) {
        console.error('Failed to fetch customer contact phone numbers:', error);
      }
    };

    fetchPhoneNumbers();
  }, [currentUser]);

  useEffect(() => {
    if (state === PhoneChangePageState.Success) {
      accountResource.logoutWithConfigUrl(getConfig().openamLogoutUrl);
    }
  }, [state]);

  const exitButtonAction = () => {
    if (isWebView) {
      window.location.href = getConfig().closeWebViewUrl;
    } else {
      AppRoutes.goToYettelAccount();
    }
  };

  // Figyeljük a VerificationComponent eseményeit
  useEffect(() => {
    return onWidgetEvent(async (data: any, token: string) => {
      console.log('[PhoneChange] WidgetEvent received:', data.eventName, data, 'state:', state);
      switch (data.eventName) {
        case WidgetEvent.ProcessFinished:
          if (!data.isSuccess && data.eventValue?.banned) {
            setState(PhoneChangePageState.Banned);
            return;
          }
          if (state === PhoneChangePageState.PhoneVerification) {
            if (data.isSuccess) {
              setVerifiedMsisdn(data.eventValue?.msisdn || '');
              setVerificatorJwtMsisdn(token || '');
              setState(PhoneChangePageState.EmailVerification);
            } else {
              setState(PhoneChangePageState.Error);
            }
          } else if (state === PhoneChangePageState.EmailVerification) {
            if (data.isSuccess) {
              try {
                await accountResource.updateAccountCtn(
                  currentUser?.accountId || '',
                  verifiedMsisdn,
                  verificatorJwtMsisdn,
                  token,
                );
                setState(PhoneChangePageState.Success);
              } catch (error) {
                console.error('Failed to update account CTN:', error);
                setState(PhoneChangePageState.Error);
              }
            } else {
              setState(PhoneChangePageState.Error);
            }
          }
          break;
      }
    });
  }, [currentUser, state, verifiedMsisdn, verificatorJwtMsisdn]);

  const lastHeader = useRef('');
  const header = useMemo(() => {
    let result = '';
    if (view === PhoneChangePhase.PhoneNumberVerificationCA) {
      result = translation.changePhone.flow.header.selectPhone;
    } else if (view === PhoneChangePhase.PhoneNumberVerificationOther) {
      result = translation.changePhone.flow.header.differentPhone;
    } else if (view === PhoneChangePhase.PhoneNumberVerificationCode) {
      result = translation.changePhone.flow.header.confirmPhone;
    } else if (view === PhoneChangePhase.Error) {
      result = '';
    }
    if (result) lastHeader.current = result;
    return lastHeader.current;
  }, [view, translation]);

  //TODO: ha nem kell useMemo lehet egyszerűsíteni
  const lastText = useRef('');
  const text = useMemo(() => {
    let result = '';
    if (view === PhoneChangePhase.PhoneNumberVerificationCA) {
      result = translation.changePhone.flow.text.selectPhone;
    } else if (view === PhoneChangePhase.PhoneNumberVerificationOther) {
      result = translation.changePhone.flow.text.differentPhone;
    } else if (view === PhoneChangePhase.PhoneNumberVerificationCode) {
      result = translation.changePhone.flow.text.confirmPhone;
    } else if (view === PhoneChangePhase.Error) {
      result = '';
    }
    if (result) lastText.current = result;
    return lastText.current;
  }, [view, translation]);

  const successBoxConfig = useMemo(
    () => ({
      ...successIcon,
      headerHTML: translation.changePhone.flow.header.success,
      subHeader: isWebView ? translation.changePhone.flow.text.successApp : translation.changePhone.flow.text.success,
      action: (
        <Button
          onClick={() =>
            isWebView
              ? (window.location.href = getConfig().appLogoutUrl)
              : (window.location.href = getConfig().webLoginUrl)
          }
        >
          {translation.changePhone.goToLogin}
        </Button>
      ),
    }),
    [translation, isWebView],
  );

  const errorBoxConfig = useMemo(
    () => ({
      ...errorIcon,
      headerHTML: translation.changePhone.flow.header.failed,
      subHeader: translation.changePhone.flow.text.failed,
      action: (
        <Button onClick={() => setState(PhoneChangePageState.PhoneVerification)}>
          {translation.changePhone.retry}
        </Button>
      ),
    }),
    [translation],
  );

  const backButtonAction = () => {
    if (state === PhoneChangePageState.EmailVerification) {
      setState(PhoneChangePageState.PhoneVerification);
    } else if (view === PhoneChangePhase.PhoneNumberVerificationCA) {
      if (isWebView) {
        window.location.href = getConfig().closeWebViewUrl;
      } else {
        AppRoutes.goToYettelAccount();
      }
    }
  };

  const BackButton = () => {
    return (
      <Button
        variant="text-undecorated"
        onClick={backButtonAction}
      >
        {translation.changePhone.buttonTextBack}
      </Button>
    );
  };

  // Success screen
  if (state === PhoneChangePageState.Success) {
    return (
      <div className={`account-flow${isWebView ? ' account-flow--webview' : ''}`}>
        <div className={`account-flow__container${isWebView ? ' account-flow__container--webview' : ''}`}>
          {isWebView ? (
            <div className="account-flow__finish">
              <SuccessIcon />
              <h3>{translation.changePhone.flow.header.success}</h3>
              <p
                dangerouslySetInnerHTML={{
                  __html: isWebView
                    ? translation.changePhone.flow.text.successApp
                    : translation.changePhone.flow.text.success,
                }}
              ></p>
              <Button
                variant="contained"
                onClick={() => (window.location.href = getConfig().appLogoutUrl)}
              >
                {translation.changePassword.continueToAccount}
              </Button>
            </div>
          ) : (
            <Box {...successBoxConfig} />
          )}
        </div>
      </div>
    );
  }

  // Error screen
  if (state === PhoneChangePageState.Error) {
    return (
      <div className={`account-flow${isWebView ? ' account-flow--webview' : ''}`}>
        <div className={`account-flow__container${isWebView ? ' account-flow__container--webview' : ''}`}>
          {isWebView ? (
            <div className="account-flow__finish">
              <FailedIcon />
              <h3>{translation.changePhone.flow.header.failed}</h3>
              <p dangerouslySetInnerHTML={{ __html: translation.changePhone.flow.text.failed }}></p>
              <Button
                variant="contained"
                onClick={() => (window.location.href = getConfig().appRedirectBackUrl)}
              >
                {translation.changePassword.backToEdit}
              </Button>
            </div>
          ) : (
            <Box {...errorBoxConfig} />
          )}
        </div>
      </div>
    );
  }

  // Banned screen
  if (state === PhoneChangePageState.Banned) {
    return <Banned buttonAction={exitButtonAction} />;
  }

  // Email verification flow
  if (state === PhoneChangePageState.EmailVerification) {
    return (
      <div className={`account-flow${isWebView ? ' account-flow--webview' : ''}`}>
        <div className={`account-flow__container${isWebView ? ' account-flow__container--webview' : ''}`}>
          <h3>
            {isWebView
              ? translation.changePhone.flow.header.emailVerificationApp
              : translation.changePhone.flow.header.emailVerification}
          </h3>
          <p
            dangerouslySetInnerHTML={
              isWebView
                ? { __html: translation.changePhone.flow.text.emailVerificationApp }
                : { __html: translation.changePhone.flow.text.emailVerification }
            }
          ></p>
          <VerificationComponent
            initParamsJWT={emailInitParamsJWT}
            showFinishScreen={false}
            skipGetEmail
            initialEmail={currentUser?.email || ''}
            disableBackButton
          />
          <BackButton />
        </div>
      </div>
    );
  }

  // Phone verification flow
  return (
    <div className={`account-flow${isWebView ? ' account-flow--webview' : ''}`}>
      <div className={`account-flow__container${isWebView ? ' account-flow__container--webview' : ''}`}>
        <h3>{header}</h3>
        <p dangerouslySetInnerHTML={{ __html: text }}></p>
        <VerificationComponent
          initParamsJWT={phoneInitParamsJWT}
          showFinishScreen={false}
          initialPhoneNumber={phoneNumbers}
        />
        {view === PhoneChangePhase.PhoneNumberVerificationCA && <BackButton />}
      </div>
    </div>
  );
};
